import { cloudflareLoader } from 'components/CloudflareResizedImage/CloudflareResizedImage';
import type { CameraForecastResponse } from 'types/camera';

export const CAM_MATCH_GRID_THUMB_WIDTH = 186;
export const CAM_MATCH_GRID_THUMB_HEIGHT = 104;
export const CAM_MATCH_GRID_THUMB_QUALITY = 75;
export const CAM_MATCH_CARD_PADDING = 6;
export const CAM_MATCH_GRID_GAP = 3;

export const CAM_MATCHES_GRID_OUTER_WIDTH =
  CAM_MATCH_CARD_PADDING * 2 + CAM_MATCH_GRID_GAP + CAM_MATCH_GRID_THUMB_WIDTH * 2;

export const CAM_MATCH_EXPANDED_HERO_WIDTH =
  CAM_MATCHES_GRID_OUTER_WIDTH - CAM_MATCH_CARD_PADDING * 2;
export const CAM_MATCH_EXPANDED_HERO_HEIGHT = Math.round((CAM_MATCH_EXPANDED_HERO_WIDTH * 9) / 16);
export const CAM_MATCH_EXPANDED_HERO_QUALITY = 85;

export const CAM_MATCH_MODAL_IMAGE_WIDTH = 774;
export const CAM_MATCH_MODAL_IMAGE_HEIGHT = Math.round((CAM_MATCH_MODAL_IMAGE_WIDTH * 9) / 16);
export const CAM_MATCH_MODAL_IMAGE_QUALITY = 85;
export const CAM_MATCH_MODAL_TABLET_LG = 976;

export const CAM_MATCH_PREFETCH_CAP = 200;
export const CAM_MATCHES_EXPANDED_STORAGE_KEY = 'sl_cam_matches_isExpanded';

export const gridThumbLoader = cloudflareLoader({ fit: 'cover' });
const expandedHeroLoader = cloudflareLoader({ fit: 'cover' });

export function buildCamMatchGridThumbSrc(rawUrl: string): string {
  return gridThumbLoader({
    src: rawUrl,
    width: CAM_MATCH_GRID_THUMB_WIDTH,
    quality: CAM_MATCH_GRID_THUMB_QUALITY,
  });
}

/**
 * Next.js legacy Image with layout="fill" generates srcset from the combined
 * imageSizes + deviceSizes list. For sizes="186px", the browser picks 256w (1×),
 * 384w (2×), or 640w (3×). Build the same srcset so hidden preloader <img> elements
 * warm the exact cache entries the grid will request.
 */
export const CAM_MATCH_GRID_PRELOAD_WIDTHS = [256, 384, 640] as const;

export function buildCamMatchGridThumbSrcset(rawUrl: string): string {
  return CAM_MATCH_GRID_PRELOAD_WIDTHS.map(
    (w) =>
      `${gridThumbLoader({ src: rawUrl, width: w, quality: CAM_MATCH_GRID_THUMB_QUALITY })} ${w}w`,
  ).join(', ');
}

export function buildCamMatchExpandedHeroSrc(rawUrl: string): string {
  return expandedHeroLoader({
    src: rawUrl,
    width: CAM_MATCH_EXPANDED_HERO_WIDTH,
    quality: CAM_MATCH_EXPANDED_HERO_QUALITY,
  });
}

export function collectCamMatchThumbnailRawUrls(data: CameraForecastResponse): string[] {
  const comparisons = data.futureCam?.forecastComparisons;
  if (!comparisons?.length) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of comparisons) {
    for (const m of c.topMatches ?? []) {
      for (const u of m.multipleThumbnailUrls ?? []) {
        if (typeof u === 'string' && u.length > 0 && !seen.has(u)) {
          seen.add(u);
          out.push(u);
          if (out.length >= CAM_MATCH_PREFETCH_CAP) {
            return out;
          }
        }
      }
    }
  }
  return out;
}

/**
 * Deduped primary thumbnail URLs (multipleThumbnailUrls[0]) for every match across
 * all forecast hours. Used by the grid preloader — the grid only ever shows [0],
 * so preloading all URLs for the modal carousel would waste bandwidth.
 * No cap: 5 days × 24 h × 4 matches = ~480 small images, all needed.
 */
export function collectCamMatchGridPrimaryUrls(data: CameraForecastResponse): string[] {
  const comparisons = data.futureCam?.forecastComparisons;
  if (!comparisons?.length) {
    return [];
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of comparisons) {
    for (const m of c.topMatches ?? []) {
      const u = m.multipleThumbnailUrls?.[0];
      if (typeof u === 'string' && u.length > 0 && !seen.has(u)) {
        seen.add(u);
        out.push(u);
      }
    }
  }
  return out;
}

/**
 * Like collectCamMatchGridPrimaryUrls but restricted to the given 0-based day
 * indices (0 = forecast day 0 / today). Day index is derived by comparing each
 * comparison's datetime against the UTC midnight of the first comparison.
 */
export function collectCamMatchGridPrimaryUrlsForDays(
  data: CameraForecastResponse,
  visibleDayIndices: ReadonlySet<number>,
): string[] {
  const comparisons = data.futureCam?.forecastComparisons;
  if (!comparisons?.length || !visibleDayIndices.size) return [];

  const day0Start = comparisons[0].datetime; // already at local midnight day 0

  const seen = new Set<string>();
  const out: string[] = [];
  for (const c of comparisons) {
    const dayIndex = Math.floor((c.datetime - day0Start) / 86400);
    if (!visibleDayIndices.has(dayIndex)) continue;
    for (const m of c.topMatches ?? []) {
      const u = m.multipleThumbnailUrls?.[0];
      if (typeof u === 'string' && u.length > 0 && !seen.has(u)) {
        seen.add(u);
        out.push(u);
      }
    }
  }
  return out;
}

function pickPreloadWidth(): number {
  const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1;
  if (dpr > 2.5) return 640;
  if (dpr > 1.5) return 384;
  return 256;
}

/** Warm the HTTP cache for grid thumbnail URLs at the device-appropriate srcset width. */
export function prefetchCamMatchGridUrls(rawUrls: string[]): void {
  if (typeof window === 'undefined' || !rawUrls.length) return;
  const width = pickPreloadWidth();
  for (const url of rawUrls) {
    const img = new Image();
    img.src = gridThumbLoader({ src: url, width, quality: CAM_MATCH_GRID_THUMB_QUALITY });
  }
}

/** Warm HTTP cache for the expanded hero still at display width. */
export function prefetchCamMatchExpandedHero(rawUrl: string): void {
  if (!rawUrl || typeof window === 'undefined') {
    return;
  }
  const img = new Image();
  img.src = buildCamMatchExpandedHeroSrc(rawUrl);
}
